/**
 * The Squares part of the Settings tab (ISSUE-1274, screens 12, 13),
 * rendered at the top of the Settings tab by
 * `features/poolScreen/PoolSettingsTabPane.tsx`. The generic Chat,
 * Notifications and pool settings cards stay below it.
 *
 * Pool card ("Set at creation": league, grid type, games, and + Add a grid),
 * Grid rules (squares on grid, axes and checkpoints lock after the first
 * lock; the rest stay editable), When a new grid is created (Classic), New
 * season (enabled once every grid is final), and "How this pool works".
 * Everyone reads the Pool card and the rules; only admins edit.
 */
import {
  useSquaresPoolGet,
  useSquaresPresetsGet,
  useSquaresSeasonUpdate,
} from "@sportsmet/contest-sdk/private";
import { Button, Card, InlineBanner, SavedNote, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { SquaresAddGridSheet, SquaresSummaryRow } from "./SquaresAddGridSheet";
import { runSquaresMutation } from "./squaresActions";
import {
  SQUARES_GAME_ERROR,
  SQUARES_GAME_TRY_AGAIN,
  SQUARES_GRID_TYPE_COPY,
  SQUARES_SETTINGS_ADD_GRID,
  SQUARES_SETTINGS_GAMES,
  SQUARES_SETTINGS_GRID_RULES,
  SQUARES_SETTINGS_GRID_TYPE,
  SQUARES_SETTINGS_LEAGUE,
  SQUARES_SETTINGS_LOADING,
  SQUARES_SETTINGS_NEW_SEASON,
  SQUARES_SETTINGS_NEW_SEASON_READY,
  SQUARES_SETTINGS_NEW_SEASON_WAITING,
  SQUARES_SETTINGS_POOL_CARD,
  SQUARES_SETTINGS_SEASON,
  SQUARES_SETTINGS_SET_AT_CREATION,
  SQUARES_SETTINGS_START_NEW_SEASON,
  SQUARES_EMPTY_NO_SEASON,
  squaresGamesSummary,
  squaresSeasonLine,
} from "./squaresCopy";
import { SquaresGridRulesFields } from "./SquaresGridRulesFields";
import { SquaresNewSeasonSheet } from "./SquaresNewSeasonSheet";
import { SquaresRulesCard } from "./SquaresRulesCard";
import type {
  typeSquaresAxes,
  typeSquaresGridSettings,
  typeSquaresNewGridRules,
  typeSquaresSeason,
} from "./squaresTypes";
import { useSquaresLeagueTeams } from "./useSquaresLeagueTeams";

type typeSeasonPatch = {
  maxPerMember?: number | null;
  autoLock?: boolean;
  showNumbers?: boolean;
  carrySquares?: boolean;
  redrawNumbers?: boolean;
  autoPublish?: boolean;
  size?: number;
  axes?: typeSquaresAxes;
  checkpoints?: string[];
};

/** Only the fields that changed, so a frozen field is never sent back. */
export function squaresGridSettingsPatch(input: {
  current: typeSquaresGridSettings;
  next: typeSquaresGridSettings;
}): typeSeasonPatch {
  const patch: typeSeasonPatch = {};
  if (input.next.size !== input.current.size) patch.size = input.next.size;
  if (
    (input.next.maxPerMember ?? null) !== (input.current.maxPerMember ?? null)
  ) {
    patch.maxPerMember = input.next.maxPerMember ?? null;
  }
  if (input.next.autoLock !== input.current.autoLock) {
    patch.autoLock = input.next.autoLock;
  }
  if (input.next.showNumbers !== input.current.showNumbers) {
    patch.showNumbers = input.next.showNumbers;
  }
  return patch;
}

export function squaresNewGridPatch(input: {
  current: typeSquaresNewGridRules;
  next: typeSquaresNewGridRules;
}): typeSeasonPatch {
  const patch: typeSeasonPatch = {};
  if (input.next.carrySquares !== input.current.carrySquares) {
    patch.carrySquares = input.next.carrySquares;
  }
  if (input.next.redrawNumbers !== input.current.redrawNumbers) {
    patch.redrawNumbers = input.next.redrawNumbers;
  }
  if (input.next.autoPublish !== input.current.autoPublish) {
    patch.autoPublish = input.next.autoPublish;
  }
  return patch;
}

export const SquaresSettingsSection = ({
  poolId,
}: typePoolTypePaneProps): ReactNode => {
  const [sheet, setSheet] = useState<"addGrid" | "newSeason" | null>(null);
  const [isSaved, setSaved] = useState(false);
  const poolQuery = useSquaresPoolGet({ poolId });
  const pool = poolQuery.data?.data ?? null;
  const season = pool?.season ?? null;
  const canManage = pool?.viewer.canManage ?? false;
  const teams = useSquaresLeagueTeams(season?.leagueKey ?? null);
  const presetsQuery = useSquaresPresetsGet(
    { leagueKey: season?.leagueKey ?? "" },
    { enabled: season !== null && canManage, staleTime: 5 * 60 * 1000 },
  );
  const presets = presetsQuery.data?.data ?? null;
  const seasonUpdate = useSquaresSeasonUpdate();

  async function save(
    current: typeSquaresSeason,
    patch: typeSeasonPatch,
  ): Promise<void> {
    if (Object.keys(patch).length === 0) return;
    setSaved(false);
    const result = await runSquaresMutation(() =>
      seasonUpdate.mutateAsync({
        poolId,
        squaresSeasonId: current.squaresSeasonId,
        ...patch,
      }),
    );
    if (result !== null) setSaved(true);
  }

  if (poolQuery.isLoading) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="squares-settings-section"
        aria-label={SQUARES_SETTINGS_LOADING}
      >
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
    );
  }
  if (pool === null) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="squares-settings-section"
      >
        <InlineBanner
          variant="warning"
          message={SQUARES_GAME_ERROR}
          action={{
            label: SQUARES_GAME_TRY_AGAIN,
            onAction: () => {
              void poolQuery.refetch();
            },
          }}
        />
      </div>
    );
  }
  if (season === null) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="squares-settings-section"
      >
        <Card>
          <p className="text-body text-ink-secondary">
            {SQUARES_EMPTY_NO_SEASON}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="squares-settings-section"
      data-pool-id={poolId}
    >
      <Card
        title={SQUARES_SETTINGS_POOL_CARD}
        data-testid="squares-settings-pool-card"
        badge={
          <span className="rounded-full border border-line px-2 text-small text-ink-secondary">
            {SQUARES_SETTINGS_SET_AT_CREATION}
          </span>
        }
      >
        <dl className="flex flex-col divide-y divide-hairline">
          <SquaresSummaryRow
            label={SQUARES_SETTINGS_LEAGUE}
            value={teams.leagueName}
          />
          <SquaresSummaryRow
            label={SQUARES_SETTINGS_GRID_TYPE}
            value={SQUARES_GRID_TYPE_COPY[season.gridType].title}
          />
          <SquaresSummaryRow
            label={SQUARES_SETTINGS_GAMES}
            value={squaresGamesSummary({
              gamesLabel: season.gamesLabel,
              gameCount: season.gameCount,
              windowLabel: season.gamesWindowLabel ?? null,
            })}
          />
          <SquaresSummaryRow
            label={SQUARES_SETTINGS_SEASON}
            value={squaresSeasonLine(season.seasonNumber)}
          />
        </dl>
        {canManage ? (
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            data-testid="squares-settings-add-grid"
            onClick={() => {
              setSheet("addGrid");
            }}
          >
            {SQUARES_SETTINGS_ADD_GRID}
          </Button>
        ) : null}
      </Card>

      {canManage && presets !== null ? (
        <Card
          title={SQUARES_SETTINGS_GRID_RULES}
          data-testid="squares-settings-grid-rules"
          action={<SavedNote saved={isSaved} />}
        >
          <SquaresGridRulesFields
            presets={presets}
            teamsById={teams.teamsById}
            frozenFields={season.frozenFields}
            isNewGridShown={season.gridType === "classic"}
            isCustomAxesOffered={false}
            value={{
              gridType: season.gridType,
              axes: season.axes,
              checkpoints: season.checkpoints.map(
                (checkpoint) => checkpoint.checkpointKey,
              ),
              gridSettings: season.gridSettings,
              newGrid: season.newGrid,
            }}
            onChangeAxes={(axes) => {
              if (axes.kind === "custom") return;
              void save(season, { axes });
            }}
            onChangeCheckpoints={(checkpoints) => {
              void save(season, { checkpoints: [...checkpoints] });
            }}
            onChangeGridSettings={(next) => {
              void save(
                season,
                squaresGridSettingsPatch({
                  current: season.gridSettings,
                  next,
                }),
              );
            }}
            onChangeNewGrid={(next) => {
              void save(
                season,
                squaresNewGridPatch({ current: season.newGrid, next }),
              );
            }}
          />
        </Card>
      ) : null}

      {canManage ? (
        <Card
          title={SQUARES_SETTINGS_NEW_SEASON}
          data-testid="squares-settings-new-season"
        >
          <p className="text-body text-ink-secondary">
            {season.canStartNewSeason
              ? SQUARES_SETTINGS_NEW_SEASON_READY
              : SQUARES_SETTINGS_NEW_SEASON_WAITING}
          </p>
          <Button
            variant="secondary"
            disabled={!season.canStartNewSeason}
            data-testid="squares-settings-start-season"
            onClick={() => {
              setSheet("newSeason");
            }}
          >
            {SQUARES_SETTINGS_START_NEW_SEASON}
          </Button>
        </Card>
      ) : null}

      <SquaresRulesCard poolId={poolId} />

      {canManage ? (
        <>
          <SquaresAddGridSheet
            open={sheet === "addGrid"}
            onClose={() => {
              setSheet(null);
            }}
            poolId={poolId}
            season={season}
            grids={pool.grids}
            teams={teams.teams}
            teamsById={teams.teamsById}
          />
          <SquaresNewSeasonSheet
            open={sheet === "newSeason"}
            onClose={() => {
              setSheet(null);
            }}
            poolId={poolId}
            season={season}
            teamsById={teams.teamsById}
          />
        </>
      ) : null}
    </div>
  );
};
